import React, { useState, useMemo } from 'react';
import { CheckCircle2, Circle, ListChecks, ChevronRight } from 'lucide-react';

/**
 * PlanChecklistCard — turns an Architect agent's numbered task list into an
 * interactive checkbox card. Purely client-side state (checking a box does
 * NOT mutate anything on the server — it's a UX affordance for the user to
 * track their own progress).
 *
 * Detects the checklist by looking for consecutive lines starting with
 * "1.", "2.", ... or "- [ ]", "- [x]" markers in the assistant's markdown.
 *
 * Renders as a collapsible card at the TOP of the assistant bubble so the
 * user always sees the plan skeleton even if the model wrote a long prose
 * explanation below.
 */

const NUMBERED_LINE = /^\s*(\d+)\.\s+(.+)$/;
const CHECKBOX_LINE = /^\s*-\s*\[([ xX])\]\s+(.+)$/;

export function extractChecklistItems(markdown) {
  if (!markdown || typeof markdown !== 'string') return [];
  const lines = markdown.split('\n');
  const items = [];
  let started = false;
  let sawBlank = 0;
  for (const line of lines) {
    let m = line.match(CHECKBOX_LINE);
    if (m) {
      items.push({ text: m[2].trim(), preChecked: m[1].toLowerCase() === 'x' });
      started = true;
      sawBlank = 0;
      continue;
    }
    m = line.match(NUMBERED_LINE);
    if (m) {
      items.push({ text: m[2].trim(), preChecked: false });
      started = true;
      sawBlank = 0;
      continue;
    }
    // Allow up to 2 blank lines between numbered items (models often
    // paragraph-space them). Break only on the 3rd blank or on any
    // real non-list text after we already have items.
    if (started) {
      if (line.trim() === '') { sawBlank += 1; if (sawBlank >= 3) break; continue; }
      // Continuation lines of the current item (indented) — skip, don't break.
      if (/^\s{2,}\S/.test(line)) continue;
      // Any other non-list text ends the list.
      break;
    }
  }
  // Require at least 3 items to be treated as a real plan (avoid catching
  // stray "1. Fixed foo" one-liners).
  return items.length >= 3 ? items : [];
}

export function PlanChecklistCard({ markdown, turnId }) {
  const items = useMemo(() => extractChecklistItems(markdown), [markdown]);
  const [checked, setChecked] = useState(() => new Set(
    items.map((it, i) => (it.preChecked ? i : null)).filter((v) => v !== null),
  ));
  const [expanded, setExpanded] = useState(true);

  if (items.length === 0) return null;

  const done = checked.size;
  const total = items.length;
  const pct = Math.round((done / total) * 100);

  const toggle = (idx) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div
      data-testid={`plan-checklist-${turnId || 'x'}`}
      className="mb-2 rounded-lg border border-purple-800/60 bg-purple-950/30 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-purple-900/30 transition-colors"
      >
        <ListChecks className="h-3.5 w-3.5 text-purple-300 shrink-0" />
        <span className="text-[11px] font-semibold text-purple-200 tracking-wide">Plan</span>
        <span className="text-[10px] text-purple-400/80 ml-1">
          {done}/{total} · {pct}%
        </span>
        <div className="flex-1" />
        <ChevronRight
          className={`h-3.5 w-3.5 text-purple-400/70 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
      </button>
      {expanded ? (
        <ul className="border-t border-purple-800/40 p-2 space-y-1">
          {items.map((it, i) => {
            const isChecked = checked.has(i);
            return (
              <li key={i} className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  className="mt-0.5 shrink-0"
                  data-testid={`plan-check-${i}`}
                  aria-label={isChecked ? 'Uncheck' : 'Check'}
                >
                  {isChecked ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  ) : (
                    <Circle className="h-3.5 w-3.5 text-purple-400/60" />
                  )}
                </button>
                <span
                  className={`text-[11px] leading-snug ${isChecked ? 'line-through text-zinc-500' : 'text-zinc-200'}`}
                >
                  {it.text}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
